import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Database,
  BarChart3,
  Globe,
  BookOpen,
  Package,
  Phone,
  Truck,
  Ship,
  Mail,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { integrationsApi, type CreateInstanceInput } from '@/api/integrations';
import { unwrapError } from '@/api/client';
import type { IntegrationType, IntegrationTypeInfo } from '@/api/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import { Field } from './forms/shared';
import { ElasticForm } from './forms/ElasticForm';
import { RagForm } from './forms/RagForm';
import { UpelaApiForm } from './forms/UpelaApiForm';
import { ZohoForm } from './forms/ZohoForm';
import { UgoForm } from './forms/UgoForm';
import { BigqueryForm } from './forms/BigqueryForm';
import { ShipperPortalForm } from './forms/ShipperPortalForm';
import { ApiConfigForm } from './forms/ApiConfigForm';
import { EmailForm } from './forms/EmailForm';

const TYPE_ICONS: Record<IntegrationType, typeof Database> = {
  elastic: Database,
  bigquery: BarChart3,
  api: Globe,
  rag: BookOpen,
  'upela-api': Package,
  zoho: Phone,
  ugo: Truck,
  'shipper-portal': Ship,
  email: Mail,
};

const SEED_TOOL_COUNT: Partial<Record<IntegrationType, number>> = {
  elastic: 1,
  bigquery: 2,
};

function seedToolCount(type: IntegrationType | null): number {
  return type ? SEED_TOOL_COUNT[type] ?? 0 : 0;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export function AddInstanceDialog({ open, onOpenChange, onCreated }: Props): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentBotId } = useWorkspace();
  const [selectedType, setSelectedType] = React.useState<IntegrationType | null>(null);
  const [label, setLabel] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [seedTools, setSeedTools] = React.useState(true);

  const typesQuery = useQuery({
    queryKey: ['integration-types', currentBotId],
    queryFn: () => integrationsApi.types(),
    enabled: open,
    staleTime: 10_000,
  });

  React.useEffect(() => {
    if (open) {
      setSelectedType(null);
      setLabel('');
      setDescription('');
      setSeedTools(true);
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: (data: CreateInstanceInput) => integrationsApi.create(data),
    onSuccess: (res) => {
      toast.success(t('integrations.createdToast', { label: res.instance.label }));
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
      void queryClient.invalidateQueries({ queryKey: ['integration-types'] });
      onCreated();
      onOpenChange(false);
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const handleConfigSubmit = (config: Record<string, unknown>): void => {
    if (!selectedType) return;
    createMutation.mutate({
      type: selectedType,
      label: label.trim() || typesQuery.data?.types.find((t) => t.type === selectedType)?.label || selectedType,
      description: description.trim() || undefined,
      config,
      ...(seedToolCount(selectedType) ? { seedTools } : {}),
    });
  };

  const goBack = (): void => {
    setSelectedType(null);
    setLabel('');
    setDescription('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {selectedType
              ? t('integrations.addInstance.configTitle', {
                  type: typesQuery.data?.types.find((t) => t.type === selectedType)?.label ?? selectedType,
                })
              : t('integrations.addInstance.title')}
          </DialogTitle>
          <DialogDescription>
            {selectedType
              ? t('integrations.addInstance.configSubtitle')
              : t('integrations.addInstance.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {!selectedType ? (
          <TypePicker
            types={typesQuery.data?.types ?? []}
            loading={typesQuery.isLoading}
            onSelect={setSelectedType}
          />
        ) : (
          <div className="space-y-4">
            <Button variant="ghost" size="sm" onClick={goBack} className="gap-1.5 -ml-2">
              <ArrowLeft className="h-3 w-3" />
              {t('common.back')}
            </Button>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('integrations.addInstance.labelField')} htmlFor="inst-label" required>
                <Input
                  id="inst-label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder={typesQuery.data?.types.find((t) => t.type === selectedType)?.label}
                />
              </Field>
              <Field label={t('integrations.addInstance.descriptionField')} htmlFor="inst-desc">
                <Input
                  id="inst-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('integrations.addInstance.descriptionPlaceholder')}
                />
              </Field>
            </div>

            {seedToolCount(selectedType) > 0 ? (
              <div className="flex items-center gap-2 rounded-md border bg-muted/10 p-3">
                <Checkbox
                  id="inst-seed-tools"
                  checked={seedTools}
                  onCheckedChange={(c) => setSeedTools(c === true)}
                />
                <Label htmlFor="inst-seed-tools" className="cursor-pointer text-sm font-normal">
                  {t('integrations.addInstance.seedTools', {
                    count: seedToolCount(selectedType),
                    defaultValue: 'Provision the {{count}} built-in tool(s) for this integration',
                  })}
                </Label>
              </div>
            ) : null}

            <div className="max-h-[50vh] overflow-y-auto rounded border bg-muted/10 p-4">
              <ConfigForm
                type={selectedType}
                onSubmit={handleConfigSubmit}
                submitting={createMutation.isPending}
                onCancel={() => onOpenChange(false)}
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TypePicker({
  types,
  loading,
  onSelect,
}: {
  types: IntegrationTypeInfo[];
  loading: boolean;
  onSelect: (type: IntegrationType) => void;
}): JSX.Element {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {types.map((info) => {
        const Icon = TYPE_ICONS[info.type] ?? Database;
        const disabled = !info.available;
        return (
          <button
            key={info.type}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(info.type)}
            className={
              'group relative flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition-all ' +
              (disabled
                ? 'cursor-not-allowed border-muted bg-muted/30 opacity-60'
                : 'cursor-pointer border-border bg-card hover:border-primary/50 hover:shadow-md hover:shadow-primary/5')
            }
          >
            <div className="flex w-full items-center justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/20">
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex items-center gap-1.5">
                {info.type === 'api' && (
                  <Badge variant="default" className="gap-1 text-[9px] px-1.5 py-0">
                    <Sparkles className="h-2.5 w-2.5" />
                    NEW
                  </Badge>
                )}
                {disabled && (
                  <Badge variant="success" className="gap-1 text-[9px] px-1.5 py-0">
                    <CheckCircle2 className="h-2.5 w-2.5" />
                    {t('integrations.addInstance.alreadyAdded')}
                  </Badge>
                )}
                {info.maxInstances > 0 && !disabled && (
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                    {info.maxInstances === 1
                      ? t('integrations.addInstance.singletonBadge')
                      : t('integrations.addInstance.multiBadge')}
                  </Badge>
                )}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold">{info.label}</h3>
              <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                {info.description}
              </p>
            </div>
            {info.hasBundledTools && (
              <p className="text-[10px] text-muted-foreground italic">
                {t('integrations.addInstance.bundledTools')}
              </p>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ConfigForm({
  type,
  onSubmit,
  submitting,
  onCancel,
}: {
  type: IntegrationType;
  onSubmit: (config: Record<string, unknown>) => void;
  submitting: boolean;
  onCancel: () => void;
}): JSX.Element {
  const common = { submitting, onCancel };
  const wrappedSubmit = (config: unknown): void => {
    onSubmit(config as Record<string, unknown>);
  };

  switch (type) {
    case 'elastic':
      return <ElasticForm initial={null} onSubmit={wrappedSubmit} {...common} />;
    case 'rag':
      return <RagForm initial={null} onSubmit={wrappedSubmit} {...common} />;
    case 'upela-api':
      return <UpelaApiForm initial={null} onSubmit={wrappedSubmit} {...common} />;
    case 'zoho':
      return <ZohoForm initial={null} onSubmit={wrappedSubmit} {...common} />;
    case 'ugo':
      return <UgoForm initial={null} onSubmit={wrappedSubmit} {...common} />;
    case 'bigquery':
      return <BigqueryForm initial={null} onSubmit={wrappedSubmit} {...common} />;
    case 'shipper-portal':
      return <ShipperPortalForm initial={null} onSubmit={wrappedSubmit} {...common} />;
    case 'api':
      return <ApiConfigForm initial={null} onSubmit={wrappedSubmit} {...common} />;
    case 'email':
      return <EmailForm initial={null} onSubmit={wrappedSubmit} {...common} />;
  }
}
